import React, { useEffect, useState } from 'react';
import {
    collection, getDocs, query, limit, orderBy,
    startAfter, endBefore, getCountFromServer
} from 'firebase/firestore';
import { db } from '../../../firebase.config';
import '../../../styles/manageShipping.css';

const ManageShipping = () => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [firstVisible, setFirstVisible] = useState(null);
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [tempItemsPerPage, setTempItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [disableNext, setDisableNext] = useState(false);
    const [disablePrev, setDisablePrev] = useState(true);

    const fetchTotalCount = async () => {
        const snapshot = await getCountFromServer(collection(db, 'shipping'));
        setTotalItems(snapshot.data().count);
    };

    const fetchShipments = async (sortField, sortOrder, after = null, before = null) => {
        setLoading(true);
        let q = query(
            collection(db, 'shipping'),
            orderBy(sortField, sortOrder),
            limit(itemsPerPage)
        );

        if (after) q = query(q, startAfter(after));
        if (before) q = query(q, endBefore(before));

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setShipments(data);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setFirstVisible(snapshot.docs[0]);
        setDisableNext(snapshot.docs.length < itemsPerPage);
        setDisablePrev(currentPage === 1);
        setLoading(false);
    };

    useEffect(() => {
        fetchShipments(sortField, sortOrder);
        fetchTotalCount();
    }, [sortField, sortOrder, itemsPerPage]);

    const handleNext = () => {
        setCurrentPage(prev => prev + 1);
        fetchShipments(sortField, sortOrder, lastVisible);
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
            fetchShipments(sortField, sortOrder, null, firstVisible);
        }
    };

    const handleSort = (field) => {
        const order = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(order);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (e) => {
        const value = Math.max(1, parseInt(e.target.value, 10));
        setTempItemsPerPage(value);
    };

    const handleItemsPerPageBlur = () => {
        setItemsPerPage(tempItemsPerPage);
        setCurrentPage(1);
    };

    const formatDate = (createdAt) => {
        if (!createdAt) return 'N/A';
        const date = createdAt.seconds
            ? new Date(createdAt.seconds * 1000)
            : new Date(createdAt);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    };

    const handleDownloadLabel = (labelImage, id) => {
        const link = document.createElement('a');
        link.href = labelImage.startsWith('data:') ? labelImage : `data:application/pdf;base64,${labelImage}`;
        link.download = `label_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className='manage-shipping'>
            <h2>Manage Shipping</h2>

            <div className="controls">
                <button onClick={() => handleSort('createdAt')}>Sort by Date ({sortOrder})</button>
                <button onClick={() => handleSort('status')}>Sort by Status ({sortOrder})</button>
                <div className="orders-per-page">
                <label>Shipments per page:</label>
                    <input
                        type="number"
                        value={tempItemsPerPage}
                        onChange={handleItemsPerPageChange}
                        onBlur={handleItemsPerPageBlur}
                        style={{ marginLeft: "10px", padding: "5px", width: "80px" }}
                        min="1"
                    />
                </div>
            </div>

            {loading ? (
                <p>Loading shipments...</p>
            ) : (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Status</th>
                                <th>Shipping Fee</th>
                                <th>Date Ordered At</th>
                                <th>Label</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shipments.map(shipment => (
                                <tr key={shipment.id}>
                                    <td>{shipment.id}</td>
                                    <td>{shipment.status}</td>
                                    <td>${shipment.labelMetadata?.postage?.toFixed(2) || shipment?.shippingFee || '0.00'}</td>
                                    <td>{formatDate(shipment.createdAt)}</td>
                                    <td>
                                        <button onClick={() => handleDownloadLabel(shipment.labelImage, shipment.id)}>
                                            Download Label
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="pagination-controls" style={{ marginTop: '10px' }}>
                        <button onClick={handlePrev} disabled={disablePrev}>Previous Page</button>
                        <span style={{ margin: '0 10px' }}>Page {currentPage} of {totalPages}</span>
                        <button onClick={handleNext} disabled={disableNext}>Next Page</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ManageShipping;
